import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import {
	AddToCrmFormSchema,
	AddToCrmFormValues,
	CrmEntityType,
	CrmProvider,
} from "@/pages/Private/redux/crm/crm.schema";
import { useGetZohoOwnersQuery } from "@/pages/Private/redux/zoho/zoho.api";
import { useGetHubspotOwnersQuery } from "@/pages/Private/redux/hubspot/hubspot.api";
import { useGetPipedriveOwnersQuery } from "@/pages/Private/redux/pipedrive/pipedrive.api";
import {
	useGetDynamicsMarketingConfigQuery,
	useGetDynamicsOwnersQuery,
} from "@/pages/Private/redux/dynamics/dynamics.api";
import { useGetLeadRelationshipQuery } from "@/pages/Private/redux/list/list-relationship.api";
import { useGetSalesforceOwnersQuery } from "@/pages/Private/redux/salesforce/salesforce.api";

import {
	AddToCrmListOption,
	buildAddToCrmListOptions,
	pickDefaultListOption,
} from "./addToCrmListOptions";
import {
	AddToCrmEntityKey,
	PROVIDER_ENTITY_ORDER,
	applyEntityRule,
	getDefaultValues,
	isEntityLocked,
	toEntities,
} from "./addToCrmRules";

export interface AddToCrmOwner {
	id: string;
	name: string;
}

interface AddToCrmModalProps {
	isOpen: boolean;
	isLoading?: boolean;
	provider?: CrmProvider;
	selectedCount: number;
	/**
	 * The list membership the surface is showing (the Inbox thread's list person). Its
	 * presence is what turns the list picker on — surfaces that act on several prospects, or
	 * on a prospect with no list at all, pass nothing and the picker stays hidden.
	 */
	currentList?: AddToCrmListOption | null;
	onClose: () => void;
	onSubmit: (
		provider: CrmProvider,
		entities: CrmEntityType[],
		owner?: AddToCrmOwner,
		list?: AddToCrmListOption
	) => void;
}

export const AddToCrmModal: FunctionComponent<AddToCrmModalProps> = ({
	isOpen,
	isLoading,
	provider,
	selectedCount,
	currentList,
	onClose,
	onSubmit,
}) => {
	const { t } = useTranslation();
	const { watch, setValue, reset, handleSubmit } = useForm<AddToCrmFormValues>({
		resolver: zodResolver(AddToCrmFormSchema),
		defaultValues: getDefaultValues(provider),
	});
	const values = watch();

	const [owner, setOwner] = useState<AddToCrmOwner | null>(null);
	const [list, setList] = useState<AddToCrmListOption | null>(null);

	/**
	 * The picker exists to name the Dataverse marketing list / campaign the sync creates, so
	 * it is offered only where those features are switched on, for a single lead the surface
	 * can name a membership for. `selectedCount` guards the bulk surfaces: one picker cannot
	 * represent prospects that sit on different lists.
	 */
	const isDynamics = provider === CrmProvider.MSDYNAMICS;
	const { data: marketingConfig } = useGetDynamicsMarketingConfigQuery(undefined, {
		skip: !isDynamics || !currentList || selectedCount !== 1,
	});
	const marketingEnabled = Boolean(
		marketingConfig?.autoCreateMarketingLists || marketingConfig?.autoCreateCampaigns
	);
	const showListPicker = Boolean(
		isDynamics && currentList && selectedCount === 1 && marketingEnabled
	);

	const { data: relationship } = useGetLeadRelationshipQuery(
		{ listPersonId: currentList?.listPersonId ?? 0 },
		{ skip: !showListPicker || !currentList?.listPersonId }
	);

	const listOptions = useMemo(
		() => buildAddToCrmListOptions(currentList, relationship?.data?.otherLists),
		[currentList, relationship]
	);

	const listItems: AutoCompleteItem[] = useMemo(
		() => listOptions.map((option) => ({ id: option.listPersonId, title: option.listName })),
		[listOptions]
	);

	const { data: zohoOwnersData } = useGetZohoOwnersQuery(undefined, {
		skip: provider !== CrmProvider.ZOHO,
	});
	const { data: hubspotOwnersData } = useGetHubspotOwnersQuery(undefined, {
		skip: provider !== CrmProvider.HUBSPOT,
	});
	const { data: pipedriveOwnersData } = useGetPipedriveOwnersQuery(undefined, {
		skip: provider !== CrmProvider.PIPEDRIVE,
	});
	const { data: dynamicsOwnersData } = useGetDynamicsOwnersQuery(undefined, {
		skip: provider !== CrmProvider.MSDYNAMICS,
	});
	const { data: salesforceOwnersData } = useGetSalesforceOwnersQuery(undefined, {
		skip: provider !== CrmProvider.SALESFORCE,
	});

	const ownerOptions: AutoCompleteItem[] = useMemo(() => {
		if (provider === CrmProvider.ZOHO) {
			return (zohoOwnersData?.owners ?? []).map((o) => ({
				id: o.id,
				title: o.fullName?.trim() || `${o.firstName || ""} ${o.lastName || ""}`.trim() || o.email,
			}));
		}

		if (provider === CrmProvider.HUBSPOT) {
			return (hubspotOwnersData?.owners ?? []).map((o) => ({
				id: o.id,
				title: `${o.firstName || ""} ${o.lastName || ""}`.trim() || o.email,
			}));
		}

		if (provider === CrmProvider.PIPEDRIVE) {
			return (pipedriveOwnersData?.owners ?? []).map((o) => ({
				id: o.id,
				title: o.name?.trim() || o.email,
			}));
		}

		if (provider === CrmProvider.MSDYNAMICS) {
			return (dynamicsOwnersData?.owners ?? []).map((o) => ({
				id: o.id,
				title: o.name?.trim() || o.email,
			}));
		}

		if (provider === CrmProvider.SALESFORCE) {
			return (salesforceOwnersData?.owners ?? []).map((o) => ({
				id: o.id,
				title: o.name?.trim() || o.email,
			}));
		}

		return [];
	}, [
		provider,
		zohoOwnersData,
		hubspotOwnersData,
		pipedriveOwnersData,
		dynamicsOwnersData,
		salesforceOwnersData,
	]);

	useEffect(() => {
		reset(getDefaultValues(provider));
		setOwner(null);
		setList(null);
	}, [provider, reset]);

	/**
	 * Preselect once the options are known — they arrive from a request that resolves after
	 * the modal is already on screen, so this cannot happen on open.
	 *
	 * Only ever fills an empty selection: the options are rebuilt whenever the query hands
	 * back a new object, and re-applying the default on each of those would silently undo a
	 * list the user had already picked. The provider reset above clears it for a fresh open.
	 */
	useEffect(() => {
		setList((current) => current ?? pickDefaultListOption(listOptions, currentList?.listPersonId));
	}, [listOptions, currentList?.listPersonId]);

	if (!provider) {
		return null;
	}

	const setOption = (name: AddToCrmEntityKey, selected: boolean) => {
		const next = applyEntityRule(provider, values, name, selected);

		(Object.keys(next) as AddToCrmEntityKey[]).forEach((key) => setValue(key, next[key]));
	};

	const save = handleSubmit((formValues) =>
		onSubmit(
			provider,
			toEntities(formValues),
			owner ?? undefined,
			showListPicker ? (list ?? undefined) : undefined
		)
	);
	const providerKey = `crmAddTo.provider.${provider}`;

	return (
		<Modal
			handleClose={onClose}
			handleSave={save}
			disableSave={toEntities(values).length === 0}
			isLoading={isLoading}
			isOpened={isOpen}
			size="sm"
			submitButtonText={t("crmAddTo.submit")}
			title={t("crmAddTo.title", { provider: t(providerKey) })}
		>
			<div className="flex flex-col gap-4">
				<p className="text-sm text-text-meta">
					{t("crmAddTo.description", { count: selectedCount, provider: t(providerKey) })}
				</p>
				{PROVIDER_ENTITY_ORDER[provider].map((name) => (
					<div key={name} data-testid={`add-to-crm-${name}`}>
						<Checkbox
							isChecked={values[name]}
							isDisabled={isEntityLocked(provider, values, name)}
							onChange={() => setOption(name, !values[name])}
						>
							{t(`crmAddTo.entity.${name}`)}
						</Checkbox>
					</div>
				))}
				{showListPicker && (
					<div data-testid="add-to-crm-list">
						<Dropdown
							data={listItems}
							defaultValue={
								list ? listItems.find((item) => item.id === list.listPersonId) : undefined
							}
							handleSelect={(value) =>
								setList(listOptions.find((option) => option.listPersonId === value.id) ?? null)
							}
							information={t("crmAddTo.list.help")}
							isOptional={true}
							label={t("crmAddTo.list.label")}
							portal={true}
						/>
					</div>
				)}
				<div data-testid="add-to-crm-owner">
					<Dropdown
						data={ownerOptions}
						defaultValue={owner ? ownerOptions.find((o) => String(o.id) === owner.id) : undefined}
						handleSelect={(value) => setOwner({ id: String(value.id), name: value.title })}
						information={t("crmAddTo.owner.help")}
						isOptional={true}
						label={t("crmAddTo.owner.label")}
						portal={true}
					/>
				</div>
				<p className="rounded-lg bg-brand-white px-3 py-2 text-xs text-text-meta">
					{t(`crmAddTo.help.${provider}`)}
				</p>
			</div>
		</Modal>
	);
};
